import { Grid, Skeleton } from "@mui/material";
import { BoardColumnHead } from "./board.column.head";
import { Column, TransfertCard } from "../interface";
import { BoardCard } from "./board.card";
import { useCallback } from "react";
import { useColumn } from "../hooks/column";
import { DRAG_KEY } from "../constants";
import { useDrop } from "../hooks/dragAndDrop";

type BoardColumnProps = {
  column: Column;
};

export function BoardColumn({ column }: BoardColumnProps) {
  const { candidates, hasNextPage, isError, isLoading, moveCandidate } = useColumn(column);

  const { dropProps } = useDrop({
    actionKey: DRAG_KEY,
    onDrop: (cardInfo: TransfertCard) => {
      moveCandidate(cardInfo.candidate, cardInfo.from, column.id);
    },
    disabled: !column.editable,
  });

  const renderSkeleton = useCallback(
    () => (
      <Skeleton
        variant="rectangular"
        sx={{
          width: "220px",
          minWidth: "220px",
          maxWidth: "220px",
          height: "100%",
        }}
      />
    ),
    []
  );

  const renderList = useCallback(() => {
    if (isLoading || hasNextPage || isError) {
      return renderSkeleton();
    }
    return (
      <Grid
        container
        direction="column"
        wrap="nowrap"
        sx={{
          height: "100%",
          overflowY: "auto",
          gap: 2.5,
        }}
      >
        {candidates?.map((candidate) => (
          <BoardCard
            key={candidate.id}
            candidate={candidate}
            timestampType={column.user_timestamp_display}
            from={column.id}
            draggable={column.editable}
          />
        ))}
      </Grid>
    );
  }, [
    isLoading,
    hasNextPage,
    isError,
    candidates,
    renderSkeleton,
    column.id,
    column.editable,
    column.user_timestamp_display
  ]);

  return (
    <Grid
      item
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
        alignItems: "start",
        gap: 2.5,
        opacity: column.looks_disabled ? 0.5 : 1,
        width: "220px",
        minWidth: "220px",
        maxWidth: "220px",
      }}
      {...dropProps}
    >
      <BoardColumnHead column={column} total={hasNextPage ? column.total_candidates : candidates.length} />
      {renderList()}
    </Grid>
  );
}
